import * as React from 'react';

import { ApiError } from '@/domains/api';
import { authApi } from './api';
import type { DeliveryRefusal, IMailDelivery } from './types';

/**
 * The delivery allowlist, as a form that causes a mail meets it — survey-api's mail plan,
 * R1–R4 (`2026-09-23-mail-delivery-design.md` in that repo).
 *
 * A test environment runs on a copy of production — real advisors at their real addresses —
 * so everywhere but production survey-api mails only a list of domains, and the six requests
 * that cause a mail take a second address, `deliverTo`: the **test inbox** a tester names
 * while typing the advisor's own address, and the one the mail goes to instead. A request for an
 * invitation to sign in, a new seat, staff's invitation (SR-476), a registration's completion, a
 * request for another address confirmation and an address set by staff (SR-416) are those six,
 * and every screen sending one draws the field through `useTestInbox`. An advisor's invitation
 * to an assistant takes none (SR-487).
 *
 * The completion is the one of the six that is never refused for leaving it empty (survey-api's
 * self-service plan, R12): its mail is a side effect of registering, so survey-api withholds the
 * mail rather than refuse the registration. A named inbox is judged there as everywhere.
 *
 * ## The deployment says whether, never this app
 *
 * The same image is promoted from dev to prod and nothing in `runtime-config.ts` names the
 * environment — survey-api's own label for it is configuration for the same reason, since
 * `icp-dev` and `icp-uat` both run as `Development`. So whether to draw the field is
 * `GET /auth/mail-delivery`'s answer and nothing else, and the field is labelled for the
 * environments that answer `redirect: true`: dev and UAT.
 *
 * ## What is deliberately not decided here
 *
 * Whether a request *needs* a test inbox — it does unless the intended address is itself at
 * an allowed domain — is survey-api's `MailRedirectPolicy`, and this app does not run it. A
 * host comparison looks too small to disagree about, and would be the first rule of the
 * server's copied to this side all the same (CLAUDE.md's rules of the code); the field says when it
 * may be left empty and the refusal says when it could not. It shapes the control and judges
 * nothing.
 */

let answer: Nullable<Promise<IMailDelivery>> = null;

/** How long a submit waits on the read before going without it. Every button that causes a
 *  mail is held until the read settles, so a read that hangs would hold all of them with
 *  nothing on screen to say why. */
const READ_TIMEOUT_MS = 10_000;

/** Read once per page load and shared, because it is configuration: the Sign-in and address
 *  cards on one account's page ask at once, and a second request would only race the first. A failed
 *  read is not kept, so the next form to ask tries again. */
function readMailDelivery(): Promise<IMailDelivery> {
  answer ??= authApi
    .mailDelivery(AbortSignal.timeout(READ_TIMEOUT_MS))
    .catch((error: unknown) => {
      answer = null;
      throw error;
    });
  return answer;
}

/** The allowlist as a sentence says it: "@invent.us, @shookresearch.com or @veritant.com".
 *  survey-api refuses to start with the mode on and no domain, so a redirecting answer names one. */
export function allowedDomainsText(domains: readonly string[]): string {
  const named = domains.map((domain) => `@${domain}`);
  return named.length > 1
    ? `${named.slice(0, -1).join(', ')} or ${named[named.length - 1]}`
    : (named[0] ?? '');
}

/**
 * One refused `deliverTo`, in words. survey-api sends the rule and never the value, as the
 * one message of a validation problem on the field — so the sentence is built here, around
 * the address the mail was meant for. The domains are unknown when the read failed, which is
 * the case a refusal is the first news of the mode.
 */
function refusalText(
  reason: string,
  delivery: Nullable<IMailDelivery>,
  intended: string
): string {
  const domains = delivery?.redirect ? allowedDomainsText(delivery.allowedDomains) : '';
  const meantFor = intended.trim() || 'the address above';

  switch (reason as DeliveryRefusal) {
    case 'missing':
      return domains
        ? `This environment mails only ${domains} addresses, so the mail for ${meantFor} ` +
            'needs a test inbox at one of them.'
        : `This environment does not mail ${meantFor}, so the mail needs a test inbox.`;
    case 'malformed':
      return 'The test inbox has to be one plain address — no name beside it, no second address.';
    case 'not_allowlisted':
      return domains
        ? `The test inbox has to end in ${domains}.`
        : 'The test inbox is not at a domain this environment mails.';
    case 'not_accepted':
      // The one refusal that contradicts what the field was drawn from: this server mails the
      // real address now. `refused` re-reads, so the field goes and a retry sends none.
      return 'This server mails the real address and takes no test inbox now. Send it again.';
    default:
      return `The test inbox was refused: ${reason}.`;
  }
}

/**
 * The deployment's mode alone, for a screen that says what happened to a mail it did not send
 * itself — the prompt after a registration, which reports what the completion was allowed to
 * mail. The same shared read as `useTestInbox`'s, so it costs no second request; `null` until
 * it lands, and for good when it fails, which draws no Dev / UAT line rather than guessing one.
 */
export function useMailDelivery(): Nullable<IMailDelivery> {
  const [delivery, setDelivery] = React.useState<Nullable<IMailDelivery>>(null);

  React.useEffect(() => {
    let live = true;
    readMailDelivery()
      .then((next) => {
        if (live) setDelivery(next);
      })
      // Nothing to set — a failed read draws no Dev / UAT line — but recorded, because it is
      // what turns the prompt's claim about a mail into a hedge.
      .catch((error: unknown) => console.warn('[Auth] Could not read the mail mode:', error));
    return () => {
      live = false;
    };
  }, []);

  return delivery;
}

export interface ITestInbox {
  /** `null` until the first read lands, and after a first read that failed — both draw no
   *  field. A re-read that fails keeps the answer before it. */
  delivery: Nullable<IMailDelivery>;
  /** Whether the read has finished, either way. A submit waits on it, or a tester fast
   *  enough to beat one GET would send a request with no inbox and be refused for it — and it
   *  waits again on the re-read a refusal causes, or "send it again" would race it. */
  settled: boolean;
  value: string;
  setValue: (next: string) => void;
  /** The last refusal of the inbox, until the next submit clears it. */
  error: Nullable<string>;
  /** The request body's `deliverTo` member — see `deliverTo` below. */
  body: { deliverTo?: string };
  /** Called as a submit starts: the previous refusal is about the previous request. */
  begin: () => void;
  /**
   * Whether `failure` refused the inbox. When it did, the sentence is in `error` and the
   * caller reports nothing of its own; any other failure stays the caller's to word.
   */
  refused: (failure: unknown, intended: string) => boolean;
}

/**
 * The test inbox of one form: whether this deployment asks for one, what was typed, and what
 * survey-api said about it.
 *
 * **`deliverTo` is sent only while the deployment redirects and something was typed.** With
 * the mode off survey-api refuses any second address, `not_accepted`, and a blank one is none
 * by its own rule — so a production request carries no such member, and a read that failed
 * sends none either rather than guessing.
 *
 * **A refusal of the inbox re-reads the deployment.** Each one is evidence about the mode:
 * `not_accepted` says it is off, and the other three say it is on — which a form whose read
 * failed did not know. Re-reading is what draws or removes the field to match, at the cost of
 * one GET on a path that is already a refusal. A re-read that fails keeps the last answer:
 * losing the field a refusal is pointing at would be worse than a mode one read stale.
 */
export function useTestInbox(): ITestInbox {
  const [delivery, setDelivery] = React.useState<Nullable<IMailDelivery>>(null);
  const [settled, setSettled] = React.useState(false);
  const [reads, setReads] = React.useState(0);
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState<Nullable<string>>(null);

  React.useEffect(() => {
    let live = true;
    readMailDelivery()
      .then((next) => {
        if (live) setDelivery(next);
      })
      // Nothing to set: a first read that fails leaves `null`, and a re-read keeps the answer
      // it would have replaced (see above).
      .catch(() => undefined)
      .finally(() => {
        if (live) setSettled(true);
      });
    return () => {
      live = false;
    };
  }, [reads]);

  const named = value.trim();
  const body = delivery?.redirect && named ? { deliverTo: named } : {};

  const begin = React.useCallback(() => setError(null), []);

  const refused = React.useCallback(
    (failure: unknown, intended: string) => {
      if (!(failure instanceof ApiError) || failure.status !== 400) return false;
      const reason = failure.problem?.errors?.deliverTo?.[0];
      if (reason === undefined) return false;

      setError(refusalText(reason, delivery, intended));
      answer = null;
      setSettled(false);
      setReads((count) => count + 1);
      return true;
    },
    [delivery]
  );

  return { delivery, settled, value, setValue, error, body, begin, refused };
}
